import Severity from 'monaco-editor/esm/vs/base/common/severity.js';
import { URI } from 'monaco-editor/esm/vs/base/common/uri.js';
import { Event } from 'monaco-editor/esm/vs/base/common/event.js';
import { ProgressLocation, ViewColumn } from '../vscode/vs/workbench/api/common/extHostTypes.js';
import { StandaloneServices } from 'monaco-editor/esm/vs/editor/standalone/browser/standaloneServices.js';
import { IModelService } from 'monaco-editor/esm/vs/editor/common/services/model.js';
import { DisposableStore } from 'monaco-editor/esm/vs/base/common/lifecycle.js';
import { getExtHostServices } from './extHost.js';
import { unsupported } from '../tools.js';
class TextTabInput {
    constructor(uri) {
        this.uri = uri;
    }
}
function getTabFromModel(model, tabGroup) {
    return {
        label: model.uri.fsPath,
        group: tabGroup,
        get isActive() { return model.isAttachedToEditor(); },
        isDirty: false,
        isPinned: false,
        isPreview: false,
        input: new TextTabInput(model.uri)
    };
}
const tabGroup = {
    isActive: true,
    activeTab: undefined,
    viewColumn: ViewColumn.One,
    get tabs() {
        const modelService = StandaloneServices.get(IModelService);
        return ( modelService.getModels().map(model => getTabFromModel(model, tabGroup)));
    }
};
function create(getExtension, workspace) {
    return {
        showInformationMessage(message, ...rest) {
            const { extHostMessageService } = getExtHostServices();
            return extHostMessageService.showMessage(getExtension(), Severity.Info, message, rest[0], rest.slice(1));
        },
        showWarningMessage(message, ...rest) {
            const { extHostMessageService } = getExtHostServices();
            return extHostMessageService.showMessage(getExtension(), Severity.Warning, message, rest[0], rest.slice(1));
        },
        showErrorMessage(message, ...rest) {
            const { extHostMessageService } = getExtHostServices();
            return extHostMessageService.showMessage(getExtension(), Severity.Error, message, rest[0], rest.slice(1));
        },
        createOutputChannel(name, options) {
            const { extHostOutputService } = getExtHostServices();
            return extHostOutputService.createOutputChannel(name, options, getExtension());
        },
        withScmProgress(task) {
            const { extHostProgress } = getExtHostServices();
            return extHostProgress.withProgress(getExtension(), { location: ProgressLocation.SourceControl }, () => task({ report() { } }));
        },
        withProgress(options, task) {
            const { extHostProgress } = getExtHostServices();
            return extHostProgress.withProgress(getExtension(), options, task);
        },
        showTextDocument: async (documentOrUri, columnOrOptions, preserveFocus) => {
            const { extHostEditors } = getExtHostServices();
            const document = await (URI.isUri(documentOrUri)
                ? Promise.resolve(workspace.openTextDocument(documentOrUri))
                : Promise.resolve(documentOrUri));
            return extHostEditors.showTextDocument(document, columnOrOptions, preserveFocus);
        },
        createQuickPick() {
            const { extHostQuickOpen } = getExtHostServices();
            return extHostQuickOpen.createQuickPick(getExtension());
        },
        showQuickPick(items, options, token) {
            const { extHostQuickOpen } = getExtHostServices();
            const extension = getExtension();
            return extHostQuickOpen.showQuickPick(extension, items, options, token);
        },
        createInputBox() {
            const { extHostQuickOpen } = getExtHostServices();
            const extension = getExtension();
            return extHostQuickOpen.createInputBox(extension);
        },
        showInputBox(options, token) {
            const { extHostQuickOpen } = getExtHostServices();
            return extHostQuickOpen.showInput(options, token);
        },
        createTextEditorDecorationType(options) {
            const { extHostEditors } = getExtHostServices();
            const extension = getExtension();
            return extHostEditors.createTextEditorDecorationType(extension, options);
        },
        showWorkspaceFolderPick(options) {
            const { extHostQuickOpen } = getExtHostServices();
            return extHostQuickOpen.showWorkspaceFolderPick(options);
        },
        showOpenDialog: unsupported,
        showSaveDialog: unsupported,
        createWebviewPanel: unsupported,
        createStatusBarItem(alignmentOrId, priorityOrAlignment, priorityArg) {
            const { extHostStatusBar } = getExtHostServices();
            const extension = getExtension();
            let id;
            let alignment;
            let priority;
            if (typeof alignmentOrId === 'string') {
                id = alignmentOrId;
                alignment = priorityOrAlignment;
                priority = priorityArg;
            }
            else {
                alignment = alignmentOrId;
                priority = priorityOrAlignment;
            }
            return extHostStatusBar.createStatusBarEntry(extension, id, alignment, priority);
        },
        setStatusBarMessage(text, timeoutOrThenable) {
            const { extHostStatusBar } = getExtHostServices();
            return extHostStatusBar.setStatusBarMessage(text, timeoutOrThenable);
        },
        createTerminal: unsupported,
        registerTreeDataProvider(viewId, treeDataProvider) {
            const { extHostTreeViews } = getExtHostServices();
            const extension = getExtension();
            return extHostTreeViews.registerTreeDataProvider(viewId, treeDataProvider, extension);
        },
        createTreeView(viewId, options) {
            const { extHostTreeViews } = getExtHostServices();
            const extension = getExtension();
            return extHostTreeViews.createTreeView(viewId, options, extension);
        },
        registerWebviewPanelSerializer: unsupported,
        get activeTextEditor() {
            const { extHostEditors } = getExtHostServices();
            return extHostEditors.getActiveTextEditor();
        },
        get visibleTextEditors() {
            const { extHostEditors } = getExtHostServices();
            return extHostEditors.getVisibleTextEditors();
        },
        onDidChangeActiveTextEditor(listener, thisArg, disposables) {
            const { extHostEditors } = getExtHostServices();
            return extHostEditors.onDidChangeActiveTextEditor(listener, thisArg, disposables);
        },
        onDidChangeVisibleTextEditors(listener, thisArg, disposables) {
            const { extHostEditors } = getExtHostServices();
            return extHostEditors.onDidChangeVisibleTextEditors(listener, thisArg, disposables);
        },
        onDidChangeTextEditorSelection(listener, thisArgs, disposables) {
            const { extHostEditors } = getExtHostServices();
            return extHostEditors.onDidChangeTextEditorSelection(listener, thisArgs, disposables);
        },
        onDidChangeTextEditorVisibleRanges(listener, thisArgs, disposables) {
            const { extHostEditors } = getExtHostServices();
            return extHostEditors.onDidChangeTextEditorVisibleRanges(listener, thisArgs, disposables);
        },
        onDidChangeTextEditorOptions(listener, thisArgs, disposables) {
            const { extHostEditors } = getExtHostServices();
            return extHostEditors.onDidChangeTextEditorOptions(listener, thisArgs, disposables);
        },
        onDidChangeTextEditorViewColumn(listener, thisArg, disposables) {
            const { extHostEditors } = getExtHostServices();
            return extHostEditors.onDidChangeTextEditorViewColumn(listener, thisArg, disposables);
        },
        get terminals() {
            return unsupported();
        },
        get activeTerminal() {
            return unsupported();
        },
        onDidChangeActiveTerminal: Event.None,
        onDidOpenTerminal: Event.None,
        onDidCloseTerminal: Event.None,
        get state() {
            return unsupported();
        },
        onDidChangeWindowState(listener, thisArg, disposables) {
            const { extHostWindow } = getExtHostServices();
            return extHostWindow.onDidChangeWindowState(listener, thisArg, disposables);
        },
        registerUriHandler: unsupported,
        registerWebviewViewProvider: unsupported,
        registerCustomEditorProvider: unsupported,
        registerTerminalLinkProvider: unsupported,
        get activeColorTheme() {
            return unsupported();
        },
        onDidChangeActiveColorTheme: Event.None,
        registerFileDecorationProvider: unsupported,
        registerTerminalProfileProvider: unsupported,
        onDidChangeTerminalState: Event.None,
        tabGroups: {
            get all() {
                return [tabGroup];
            },
            activeTabGroup: tabGroup,
            onDidChangeTabGroups: Event.None,
            onDidChangeTabs(listener) {
                const modelService = StandaloneServices.get(IModelService);
                const store = new DisposableStore();
                store.add(modelService.onModelAdded((model) => {
                    listener({
                        opened: [getTabFromModel(model, tabGroup)],
                        closed: [],
                        changed: []
                    });
                }));
                store.add(modelService.onModelRemoved((model) => {
                    listener({
                        opened: [],
                        closed: [getTabFromModel(model, tabGroup)],
                        changed: []
                    });
                }));
                return store;
            },
            close: unsupported
        },
        showNotebookDocument: unsupported,
        visibleNotebookEditors: [],
        onDidChangeVisibleNotebookEditors: Event.None,
        activeNotebookEditor: undefined,
        onDidChangeActiveNotebookEditor: Event.None,
        onDidChangeNotebookEditorSelection: Event.None,
        onDidChangeNotebookEditorVisibleRanges: Event.None
    };
}
export { TextTabInput, create as default };
